import { applyEase, type KeyframeEase } from './enter-exit-presets';
import type { AdjustParams } from './media-adjust.util';

export interface AdjustKeyframe extends AdjustParams {
  /** Seconds from the selected clip's start. */
  tSeconds: number;
  /** Easing from this point to the next point. */
  ease?: KeyframeEase;
}

const CHANNELS = [
  'brightness',
  'contrast',
  'saturation',
  'temperature',
  'blur',
  'vignette',
  'hue',
] as const;

const NEUTRAL: Required<AdjustParams> = {
  brightness: 1,
  contrast: 1,
  saturation: 1,
  temperature: 0,
  blur: 0,
  vignette: 0,
  hue: 0,
};

export function resolveAdjustAt(
  base: AdjustParams | null | undefined,
  keyframes: readonly AdjustKeyframe[] | null | undefined,
  clipRelativeSeconds: number,
): AdjustParams | null {
  const frames = normalizeAdjustKeyframes(keyframes);
  if (!frames.length) return base ? { ...base } : null;
  const time = Math.max(0, clipRelativeSeconds);
  const left = [...frames].reverse().find(frame => frame.tSeconds <= time) ?? frames[0]!;
  const right = frames.find(frame => frame.tSeconds >= time) ?? frames[frames.length - 1]!;
  if (left === right || right.tSeconds <= left.tSeconds) return adjustFromFrame(left);
  const progress = applyEase(
    Math.max(0, Math.min(1, (time - left.tSeconds) / (right.tSeconds - left.tSeconds))),
    left.ease ?? 'linear',
  );
  const value: AdjustParams = {};
  for (const channel of CHANNELS) {
    const a = finite(left[channel], finite(base?.[channel], NEUTRAL[channel]));
    const b = finite(right[channel], a);
    value[channel] = a + (b - a) * progress;
  }
  return value;
}

export function upsertAdjustKeyframe(
  keyframes: readonly AdjustKeyframe[] | null | undefined,
  tSeconds: number,
  value: AdjustParams | null | undefined,
): AdjustKeyframe[] {
  const time = round3(Math.max(0, tSeconds));
  const existing = normalizeAdjustKeyframes(keyframes);
  const previous = existing.find(frame => Math.abs(frame.tSeconds - time) <= .001);
  const frame: AdjustKeyframe = { tSeconds: time, ...snapshotAdjust(value), ...(previous?.ease ? { ease: previous.ease } : {}) };
  return [...existing.filter(item => Math.abs(item.tSeconds - time) > .001), frame]
    .sort((a, b) => a.tSeconds - b.tSeconds);
}

export function removeAdjustKeyframeAt(
  keyframes: readonly AdjustKeyframe[] | null | undefined,
  tSeconds: number,
): AdjustKeyframe[] | null {
  const next = normalizeAdjustKeyframes(keyframes).filter(frame => Math.abs(frame.tSeconds - tSeconds) > .001);
  return next.length ? next : null;
}

export function setAdjustKeyframeEaseAt(
  keyframes: readonly AdjustKeyframe[] | null | undefined,
  tSeconds: number,
  ease: KeyframeEase,
): AdjustKeyframe[] | null {
  const next = normalizeAdjustKeyframes(keyframes).map(frame =>
    Math.abs(frame.tSeconds - tSeconds) <= .001 ? { ...frame, ease } : frame
  );
  return next.length ? next : null;
}

export function hasAdjustKeyframeAt(
  keyframes: readonly AdjustKeyframe[] | null | undefined,
  tSeconds: number,
): boolean {
  return normalizeAdjustKeyframes(keyframes).some(frame => Math.abs(frame.tSeconds - tSeconds) <= .001);
}

export function normalizeAdjustKeyframes(
  keyframes: readonly AdjustKeyframe[] | null | undefined,
): AdjustKeyframe[] {
  return (keyframes ?? [])
    .filter(frame => Number.isFinite(frame?.tSeconds))
    .map(frame => ({ ...frame, tSeconds: round3(Math.max(0, frame.tSeconds)) }))
    .sort((a, b) => a.tSeconds - b.tSeconds);
}

function adjustFromFrame(frame: AdjustKeyframe): AdjustParams {
  const value: AdjustParams = {};
  for (const channel of CHANNELS) value[channel] = finite(frame[channel], NEUTRAL[channel]);
  return value;
}

function snapshotAdjust(value: AdjustParams | null | undefined): AdjustParams {
  const snapshot: AdjustParams = {};
  for (const channel of CHANNELS) snapshot[channel] = finite(value?.[channel], NEUTRAL[channel]);
  return snapshot;
}

function finite(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function round3(value: number): number { return Math.round(value * 1000) / 1000; }
